import saveAs from 'file-saver';
import React, { useMemo } from 'react';
import AtlasV2ComparableSplineChart from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2ComparableSplineChart';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import convertLineChartSeriesToDelimitedData, {
  Series
} from 'src/components/Charts/GenericChart/SeriesConverters/lineChart';
import useAtlasV2DerivedMetricByWeekId from 'src/serverProxy/useAtlasV2DerivedMetricByWeekId';
import { useAppSelector } from 'src/utils/Hooks';
import { timestampToWeekId } from 'src/utils/dateUtils';
import { SplineChartV2Series } from 'src/components/BeaconV2Components/SplineChartV2';

/**
 *
 * Spline Chart at Conversion page.
 */
const AtlasV2ConversionSplineChart = () => {
  const primaryLegendDisplayName = useAppSelector((state) => state.mainTimePeriod.displayName);
  const comparisonLegendDisplayName = useAppSelector((state) => state.comparisonTimePeriod.displayName);
  const {
    primaryData,
    secondaryData,
    percentChange,
    isLoading,
    formattedPercentChange,
    formattedPrimaryTotal,
    formattedSecondaryTotal
  } = useAtlasV2DerivedMetricByWeekId('sales', 'purchaseRate');
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);

  const derivedPrimarySeries: SplineChartV2Series = useMemo(() => ({
    id: 'conversion-rate-primary',
    name: 'Conversion Rate',
    data: primaryData.map(([ts, val]) => ({ weekId: timestampToWeekId(ts), value: val }))
  }), [primaryData]);

  const derivedComparisonSeries: SplineChartV2Series = useMemo(() => ({
    id: 'conversion-rate-comparison',
    name: 'Conversion Rate (Comparison)',
    data: secondaryData.map(([ts, val]) => ({ weekId: timestampToWeekId(ts), value: val }))
  }), [secondaryData]);

  return (
    <BasePageContainer>
      <AtlasV2ComparableSplineChart
        fieldName="purchaseRate"
        indexName="sales"
        splineChartProps={{
          primarySeries: derivedPrimarySeries,
          comparisonSeries: derivedComparisonSeries
        }}
        beaconChartWithLegendOverride={(props, metricValues) => {
          return {
            ...props,
            ...metricValues,
            loading: isLoading,
            primaryLegendProps: {
              displayName: primaryLegendDisplayName,
              metric: formattedPrimaryTotal,
              change: formattedPercentChange,
              direction: getDirection(percentChange)
            },
            comparisonLegendProps: {
              displayName: comparisonLegendDisplayName,
              metric: formattedSecondaryTotal
            },
            convertSeriesToDelimitedData: () => {
              try {
                const data = convertLineChartSeriesToDelimitedData(
                  [
                    {
                      type: 'spline',
                      name: mainTimePeriod.displayName,
                      data: primaryData.map((row) => ({
                        x: row[0],
                        y: row[1],
                        marker: {
                          enabled: true
                        }
                      }))
                    } as unknown as Series,
                    {
                      type: 'spline',
                      name: comparisonTimePeriod.displayName,
                      data: secondaryData.map((row) => ({
                        x: row[0],
                        y: row[1],
                        marker: {
                          enabled: true
                        }
                      }))
                    } as unknown as Series
                  ],
                  comparisonTimePeriod.id === 'prior-period',
                  comparisonTimePeriod,
                  mainTimePeriod
                );

                const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
                saveAs(blob, `Conversion Rate.csv`);
              } catch (e) {
                console.error('error: ', e);
              }

              return Promise.resolve();
            }
          };
        }}
      />
    </BasePageContainer>
  );
};

export default AtlasV2ConversionSplineChart;
